import { resetUser } from "State/Auth/AuthReducer";
import { takeLatest } from "redux-saga/effects";
import { getType } from "typesafe-actions";
import { LANGUAGE_STORAGE_KEY } from "Translations/languageConfig";
import mixpanel from "mixpanel-browser";
import { queryClient } from "Infrastructure/ReactQuery/queryClient";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import {
  getLocalStorageItem,
  setLocalStorageItem,
  clearLocalStorage,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { clearSessionStorage } from "Utils/Storage/SessionStorage/SecureSessionStorage";
import { removeChatPushDeviceRequest } from "Api/Mutations/Chat/removeChatPushDeviceRequest";
import { disconnectStreamChat } from "Utils/Chat/streamChatClient";
import { disconnectStoriesFeed } from "Utils/Stories/storiesFeedsClient";
import { JWT_KEY } from "Utils/AuthUtils";

function resetUserFn({
  payload: { onAfterReset },
}: ReturnType<typeof resetUser>) {
  window.onbeforeunload = null;
  // Tear down the stories WebSocket — without this the Feeds client stays connected as the
  // logged-out persona until a reload or the next login.
  void disconnectStoriesFeed();
  const appLanguage = getLocalStorageItem(LANGUAGE_STORAGE_KEY);
  const betaDrawerSeen = getLocalStorageItem(LocalStorageKeys.BetaDrawerSeen);
  // Chat push is a property of this DEVICE, not the account: the opt-in and the push token
  // outlive the session so the next login neither re-prompts nor loses the token to remove.
  const chatPushPreference = getLocalStorageItem(
    LocalStorageKeys.ChatPushPreference,
  );
  const chatPushDeviceToken = getLocalStorageItem(
    LocalStorageKeys.ChatPushDeviceToken,
  );
  // Pushes for the logged-out person must stop on a shared phone. The Stream client may not
  // be connected right now (connect pending/failed, chat-less persona), so the API unregisters
  // the device for every persona server-side. Fired BEFORE the storage wipe: the request reads
  // the JWT synchronously when it is built. Best-effort — nothing here can react to a failure.
  // Only with a JWT: resetUser also runs for anonymous flows (sign-up, anonymous payment) where
  // there is nothing to authenticate. A 401 reply (expired session) cannot re-trigger logout —
  // the auth middleware in App.tsx only logs out while a JWT is stored, and the wipe below runs
  // synchronously before the response can arrive.
  if (chatPushDeviceToken && getLocalStorageItem(JWT_KEY)) {
    void removeChatPushDeviceRequest(chatPushDeviceToken).catch(
      () => undefined,
    );
  }
  clearLocalStorage();
  clearSessionStorage();
  setLocalStorageItem(LANGUAGE_STORAGE_KEY, appLanguage ?? "cs");
  if (betaDrawerSeen) {
    setLocalStorageItem(LocalStorageKeys.BetaDrawerSeen, betaDrawerSeen);
  }
  if (chatPushPreference) {
    setLocalStorageItem(
      LocalStorageKeys.ChatPushPreference,
      chatPushPreference,
    );
  }
  if (chatPushDeviceToken) {
    setLocalStorageItem(
      LocalStorageKeys.ChatPushDeviceToken,
      chatPushDeviceToken,
    );
  }
  queryClient.clear();
  // Tear down the chat websocket — logout is SPA navigation, so nothing else closes it and it
  // would otherwise keep receiving the logged-out persona's events (shared-device leak).
  void disconnectStreamChat();
  mixpanel.track("Log out");
  mixpanel.reset();
  onAfterReset?.();
}
export function* resetUserSaga() {
  yield takeLatest(getType(resetUser), resetUserFn);
}
